import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useField } from 'react-final-form';
import cn from 'classnames';
import mime from 'mime';

import { useTheme } from '../../../../../../theme';
import { useToggleable } from '../../../../../../hooks';
import { useFileConverter } from '../../../../../../Refund/Components/AddFile/hooks';
import { PassengerFileDocument } from '../../../../../hooks';
import { Props } from '../Group';
import { CdnIcon, Switch } from '@shared/ui';
import { useConfig } from '../../../../../../context';

const ChangeGroup: React.FC<Props> = props => {
	const provideConfirmDocuments = useConfig().Engine.Exare?.provideConfirmDocuments;

	if (!provideConfirmDocuments) {
		return null;
	}

	return <ChangeGroupFields {...props} />;
};

const ChangeGroupFields: React.FC<Props> = props => {
	const { t } = useTranslation('Passenger');
	const { Group: groupTheme, ChangeGroup: theme } = useTheme('PassengerForm');

	const { isOpen, toggle } = useToggleable(props.isChangeFiles);
	const { toBase64 } = useFileConverter();

	const field = props.fields[0];
	const fieldState = useField<PassengerFileDocument[]>(`${field.name}.changeFiles`, {
		type: field.type,
		validate: value => {
			if (isOpen && !value.length) {
				return t('Attach files');
			}

			return undefined;
		}
	});

	const inputFiles = fieldState.input.value;
	const isFilled = inputFiles?.length > 0;

	const showError = (fieldState.meta.modified && fieldState.meta.touched) || fieldState.meta.submitFailed;

	const onToggle = () => {
		props.onToggleChangeFiles?.(!isOpen);
		toggle();
	};

	const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files?.length) {
			try {
				const files: PassengerFileDocument[] = await Promise.all(
					Array.from(e.target.files).map(async file => {
						const name = file.name;
						const document = await toBase64(file);

						return {
							name,
							content: document.content,
							size: file.size
						};
					})
				);

				fieldState.input.onChange([...inputFiles, ...files]);
				fieldState.input.onBlur();
			} catch (err) {
				e.target.value = null;
				console.log(err);
			}
		}
	};

	const onClickPreview = (file: PassengerFileDocument) => (e: React.MouseEvent<HTMLSpanElement>) => {
		e.preventDefault();
		e.stopPropagation();

		const mimeType = mime.getType(file.name);
		const fileContent = `data:${mimeType};base64,${file.content}`;

		const win = window.open();

		win.document.write(
			`<iframe src="${fileContent}" frameborder="0" style="border:0; top:0; left:0; bottom:0; right:0; width:100%; height:100%;" allowfullscreen></iframe>`
		);
	};

	const onRemoveFile = (index: number) => (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();

		fieldState.input.onChange(inputFiles.filter((_, fileIndex) => fileIndex !== index));
		fieldState.input.onBlur();
	};

	return (
		<div className={cn(groupTheme.container, theme.container)}>
			<div className={theme.wrapper}>
				<Switch
					classes={{ root: theme.switch__root }}
					checked={isOpen}
					onChange={onToggle}
					label={
						<div className={theme.label}>
							{t('Replacement of passport based on age or change of surname upon marriage or divorce')}
							<div className={theme.description}>{t('Attach supporting document')}</div>
						</div>
					}
					labelPosition="right"
				/>
			</div>
			<Collapse in={isOpen} mountOnEnter unmountOnExit>
				<div className={theme.collapse}>
					<FormControlLabel
						classes={{ root: theme.control, label: theme.control__label }}
						control={
							<input
								className={theme.input}
								onChange={onChange}
								type="file"
								accept="image/jpeg, application/pdf"
								multiple
							/>
						}
						label={
							<>
								<div className={theme.collapse__icon}>
									<CdnIcon icon="Action/Attach file" />
								</div>
								<div className={theme.collapse__content}>
									<div className={theme.collapse__contentInfo}>
										<div className={theme.collapse__title}>
											<div className={theme.collapse__titleWrapper}>
												{isFilled && (
													<div className={theme.collapse__titleIcon}>
														<CdnIcon icon="Info/Item Icluded" />
													</div>
												)}
												{t('Attach a photocopy of the certificate')}
											</div>
										</div>
										<div className={theme.collapse__format}>
											{t('File format: jpg, pdf. File size: no more than 5MB', {
												nsSeparator: '|'
											})}
										</div>
									</div>
									{isFilled && (
										<div className={theme.collapse__files}>
											{inputFiles.map((file, index) => (
												<div
													key={index}
													className={theme.collapse__filesItem}
													onClick={onClickPreview(file)}
												>
													<span>{file.name}</span>
													<div className={theme.collapse__filesItemSize}>
														{Math.ceil(file.size / 1024)} Kb
													</div>
													<div
														className={theme.collapse__filesItemIcon}
														onClick={onRemoveFile(index)}
													>
														<CdnIcon icon="Action/Close-Clear" />
													</div>
												</div>
											))}
										</div>
									)}
									{showError && (
										<div className={theme.collapse__error}>
											{fieldState.meta.error || fieldState.meta.submitError}
										</div>
									)}
								</div>
							</>
						}
					/>
				</div>
			</Collapse>
		</div>
	);
};

export default ChangeGroup;
